// Anzeige-Instrumente für die Anlagenüberwachung (BORIS-artige Sensor-/Aktor-Visualisierungen).
//
// Reine, deterministische SVG-Komponenten — je Instrument aus dem jüngsten Wert, dem Katalog-
// Bereich [min,max] und dem Grenzwert-Zustand (normal/warn/alarm). Keine externe Chart-Bibliothek,
// damit sie im Test über Zahl/Attribute prüfbar bleiben und im Offline-Appliance-Build tragen.

import { type JSX } from 'react';
import type { LimitState } from '../views/dashboard';

// Farbe je Grenzwert-Zustand (gleiche Semantik wie die Dashboard-Kacheln).
const STATE_COLOR: Record<LimitState, string> = {
  normal: '#C49B18', // bf-gold
  warn: '#FBBF24', // amber-400
  alarm: '#F87171', // red-400
  unknown: '#7A8FAF', // bf-muted
};

export interface InstrumentInput {
  value: number | undefined;
  min: number;
  max: number;
  frac: number | null; // Position im Bereich [0,1] (aus rangeFraction), null = kein Wert
  state: LimitState;
  unit: string;
  // Grenzwert-Positionen im Bereich [0,1] für die farbigen Zonen (optional).
  loFrac?: number | null;
  hiFrac?: number | null;
}

function fmt(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(2);
  return v.toFixed(3);
}

// ── Tacho / Gauge (Halbkreis-Rundinstrument mit Zeiger + Alarmzonen) ────────────────────
export function GaugeInstrument({
  value,
  frac,
  state,
  unit,
  loFrac,
  hiFrac,
}: InstrumentInput): JSX.Element {
  const cx = 60;
  const cy = 58;
  const r = 46;
  // Halbkreis von links (180°) nach rechts (0°). Winkel für eine Position f ∈ [0,1].
  const pt = (f: number): [number, number] => {
    const theta = Math.PI * (1 - Math.max(0, Math.min(1, f))); // 0→π (links), 1→0 (rechts)
    return [cx + r * Math.cos(theta), cy - r * Math.sin(theta)];
  };
  const arc = (f0: number, f1: number): string => {
    const [x0, y0] = pt(f0);
    const [x1, y1] = pt(f1);
    return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
  };
  const lo = loFrac ?? null;
  const hi = hiFrac ?? null;
  const needle = frac === null ? null : pt(frac);

  return (
    <svg role="img" aria-label="Gauge" viewBox="0 0 120 72" className="h-full w-full">
      {/* Grundbogen */}
      <path d={arc(0, 1)} fill="none" stroke="#1A3460" strokeWidth={8} strokeLinecap="round" />
      {/* Farbige Zonen aus den Grenzwerten (falls vorhanden) */}
      {lo !== null && lo > 0 && (
        <path d={arc(0, lo)} fill="none" stroke="#F87171" strokeWidth={8} strokeLinecap="round" />
      )}
      {lo !== null && hi !== null && (
        <path d={arc(lo, hi)} fill="none" stroke="#3F6212" strokeWidth={8} />
      )}
      {hi !== null && hi < 1 && (
        <path d={arc(hi, 1)} fill="none" stroke="#F87171" strokeWidth={8} strokeLinecap="round" />
      )}
      {/* Zeiger */}
      {needle && (
        <>
          <line
            x1={cx}
            y1={cy}
            x2={needle[0].toFixed(2)}
            y2={needle[1].toFixed(2)}
            stroke={STATE_COLOR[state]}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          <circle cx={cx} cy={cy} r={3} fill={STATE_COLOR[state]} />
        </>
      )}
      {/* Wert + Einheit */}
      <text x={cx} y={70} textAnchor="middle" fontSize={13} fontWeight={600} fill={STATE_COLOR[state]}>
        {value === undefined ? '—' : fmt(value)}
        <tspan fontSize={8} fill="#7A8FAF">
          {' '}
          {unit}
        </tspan>
      </text>
    </svg>
  );
}

// ── Balken / Füllstand (vertikaler Balken, Füllstand im Bereich, Grenzwert-Färbung) ─────
export function BarInstrument({ value, frac, state, unit }: InstrumentInput): JSX.Element {
  const pct = frac === null ? 0 : Math.round(frac * 100);
  const color = STATE_COLOR[state];
  return (
    <div className="flex items-end gap-2" data-testid="instrument-bar" data-fill={pct}>
      <div className="relative h-16 w-6 overflow-hidden rounded border border-bf-border bg-bf-navy">
        <div
          className="absolute bottom-0 left-0 w-full"
          style={{ height: `${pct}%`, background: color }}
        />
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-semibold tabular-nums" style={{ color }}>
          {value === undefined ? '—' : fmt(value)}
        </span>
        <span className="text-[10px] text-bf-muted">{unit}</span>
      </div>
    </div>
  );
}

// ── LED / Statuslampe (Ampel nach Grenzwert-Zustand) ────────────────────────────────────
export function LampInstrument({ value, state, unit }: InstrumentInput): JSX.Element {
  const color = STATE_COLOR[state];
  return (
    <div className="flex items-center gap-3" data-testid="instrument-lamp" data-state={state}>
      <span
        className="inline-block h-8 w-8 shrink-0 rounded-full"
        style={{ background: color, boxShadow: `0 0 10px ${color}` }}
      />
      <div className="flex flex-col">
        <span className="text-lg font-semibold tabular-nums" style={{ color }}>
          {value === undefined ? '—' : fmt(value)}
        </span>
        <span className="text-[10px] text-bf-muted">{unit}</span>
      </div>
    </div>
  );
}

// ── Digitalanzeige (großes Ziffernfeld) ─────────────────────────────────────────────────
export function DigitalInstrument({ value, state, unit }: InstrumentInput): JSX.Element {
  const color = STATE_COLOR[state];
  return (
    <div
      className="flex items-baseline justify-center gap-1 rounded bg-bf-navy px-2 py-1.5"
      data-testid="instrument-digital"
    >
      <span className="font-mono text-2xl font-semibold tabular-nums" style={{ color }}>
        {value === undefined ? '—' : fmt(value)}
      </span>
      <span className="text-xs text-bf-muted">{unit}</span>
    </div>
  );
}
